import { ILaya } from "../../ILaya";
import { Laya } from "../../Laya";
import { Input } from "../display/Input";
import { Text } from "../display/Text";
import { Event } from "../events/Event";
import { InputManager } from "../events/InputManager";
import { Browser } from "../utils/Browser";
import { SpriteUtils } from "../utils/SpriteUtils";
import { PAL } from "./PlatformAdapters";
export class TextInputAdapter {
    constructor() {
        this._beginFlag = 0;
        this._editInline = true;
        this._enterEvent = new Event();
        this._lastTransform = {};
        Laya.addAfterInitCallback(() => {
            ILaya.stage.on(Event.MOUSE_UP, this, this.onTouchEnd);
            InputManager.onMouseDownCapture.add(this.onTouchBegin, this);
        });
    }
    begin(target, fromTouchBegin) {
        if (this.target === target || this._beginFlag !== 0)
            return Promise.resolve();
        this._beginFlag = 1;
        return (this.target ? this.end(false, this.target.editable) : Promise.resolve()).then(() => {
            this.target = target;
            ILaya.stage.focus = target;
            this.updateRestrictPattern();
            this._lastTransform.x = null;
            target.on(Event.UNDISPLAY, this, this.end);
            return this.onBegin().catch(e => {
                console.error("TextInputAdapter begin error:", e);
            });
        }).then(() => {
            if (this._editInline) {
                this.target.hideText(true);
                ILaya.stage.on(Event.KEY_DOWN, this, this.onKeyDown);
            }
            target.event(Event.FOCUS);
        }).then(() => {
            if (!fromTouchBegin) {
                this._beginFlag = 0;
                return this.onCanShowKeyboard().catch(e => {
                    console.error("TextInputAdapter begin error:", e);
                });
            }
            else {
                this._beginFlag = 2;
                return Promise.resolve();
            }
        });
    }
    end(complete, switching) {
        let target = this.target;
        if (!target)
            return Promise.resolve();
        this.target = null;
        ILaya.stage.focus = null;
        target.off(Event.UNDISPLAY, this, this.end);
        if (this._editInline)
            ILaya.stage.off(Event.KEY_DOWN, this, this.onKeyDown);
        return this.onEnd(target, !!complete, !!switching).then(() => {
            if (this._editInline)
                target.hideText(false);
            if (target.editable)
                target.event(Event.CHANGE);
            target.event(Event.BLUR);
        }).catch(e => {
            console.error("TextInputAdapter end error:", e);
        });
    }
    onBegin() {
        this.showInputElement();
        let ele = this._visEle;
        let target = this.target;
        if (ele instanceof HTMLInputElement)
            ele.type = this.target.type;
        ele.readOnly = !target.editable;
        ele.maxLength = target.maxChars <= 0 ? 1E5 : target.maxChars;
        ele.value = this.target.text;
        ele.placeholder = target.localizedPrompt;
        let style = ele.style;
        style.fontFamily = target.realFont;
        style.color = target.color;
        style.fontSize = target.fontSize + 'px';
        style.whiteSpace = (target.wordWrap ? "pre-wrap" : "nowrap");
        style.lineHeight = (target.leading + target.fontSize) + "px";
        style.fontStyle = (target.italic ? "italic" : "normal");
        style.fontWeight = (target.bold ? "bold" : "normal");
        style.textAlign = target.align;
        style.padding = "0 0";
        style.direction = Text.RightToLeft ? "rtl" : "";
        this.setPromptColor();
        this.syncTransform();
        if (this._editInline)
            ILaya.systemTimer.frameLoop(1, this, this.syncTransform);
        return Promise.resolve();
    }
    onCanShowKeyboard() {
        if (this._visEle)
            this._visEle.focus();
        return Promise.resolve();
    }
    onEnd(target, complete, switching) {
        Browser.document.body.scrollTop = 0;
        target.text = this._visEle.value;
        this._visEle.blur();
        this.hideInputElement();
        this._visEle = null;
        if (this._editInline)
            ILaya.systemTimer.clear(this, this.syncTransform);
        return Promise.resolve();
    }
    syncText() {
        if (this._visEle && this._beginFlag === 0)
            this.updateTargetText(this._visEle.value);
    }
    setText(value) {
        if (this._visEle)
            this._visEle.value = value;
    }
    setSelection(startIndex, endIndex) {
        if (this._visEle) {
            this._visEle.selectionStart = startIndex;
            this._visEle.selectionEnd = endIndex;
        }
    }
    onTouchBegin() {
        let lastFocus = ILaya.stage.focus;
        let touchTarget = InputManager.touchTarget;
        if (lastFocus != touchTarget) {
            if (touchTarget instanceof Input)
                this.begin(touchTarget, true);
            else if (lastFocus instanceof Input)
                this.end();
        }
    }
    onTouchEnd() {
        if (this._beginFlag !== 0) {
            if (this._beginFlag === 1) {
                ILaya.systemTimer.frameOnce(1, this, this.onTouchEnd);
            }
            else {
                this._beginFlag = 0;
                this.onCanShowKeyboard().catch(e => {
                    console.error("TextInputAdapter begin error:", e);
                });
            }
        }
    }
    setPromptColor() {
        this._promptStyleDOM = Browser.document.getElementById("promptStyle");
        if (!this._promptStyleDOM) {
            this._promptStyleDOM = Browser.document.createElement("style");
            this._promptStyleDOM.setAttribute("id", "promptStyle");
            Browser.document.head.appendChild(this._promptStyleDOM);
        }
        let color = this.target.promptColor;
        this._promptStyleDOM.innerText = `input::-webkit-input-placeholder, textarea::-webkit-input-placeholder {
                color: ${color}
            }
            input:-moz-placeholder, textarea:-moz-placeholder {
                color: ${color}
            }
            input::-moz-placeholder, textarea::-moz-placeholder {
                color: ${color}
            }
            input:-ms-input-placeholder, textarea:-ms-input-placeholder {
                color: ${color}
            }
        `;
    }
    updateRestrictPattern() {
        let value = this.target.restrict;
        if (value) {
            value = "[^" + value + "]";
            if (value.indexOf("^^") > -1)
                value = value.replace("^^", "");
            this._restrictPattern = new RegExp(value, "g");
        }
        else
            this._restrictPattern = null;
    }
    validateText(str) {
        if (str == null)
            str = "";
        if (!this.target.multiline)
            str = str.replace(/\r?\n/g, '');
        if (this._restrictPattern) {
            str = str.replace(/\u2006|\x27/g, "");
            if (this._restrictPattern.test(str)) {
                str = str.replace(this._restrictPattern, "");
            }
        }
        return str;
    }
    showInputElement() {
        if (!this._eInput)
            this.createElements();
        let password = this.target.type === "password";
        let multiline = this.target.multiline && !password;
        let inputElement = (multiline ? this._eTextArea : password ? this._ePassword : this._eInput);
        this._visEle = inputElement;
        this._container.appendChild(inputElement);
    }
    hideInputElement() {
        if (this._visEle && this._visEle.parentElement)
            this._visEle.remove();
    }
    updateTargetText(value) {
        let target = this.target;
        this.target = null;
        let ret = target.text != value;
        target.text = value;
        this.target = target;
        return ret;
    }
    getTargetTransform() {
        let padding = this.target.padding;
        let { x, y, scaleX, scaleY } = SpriteUtils.getTransformRelativeToWindow(this.target, padding[3], padding[0]);
        let w = this.target.width - padding[1] - padding[3];
        let h = this.target.height - padding[0] - padding[2];
        let t = this._lastTransform;
        if (x !== t.x || y !== t.y || w !== t.width || h !== t.height || scaleX !== t.scaleX || scaleY !== t.scaleY) {
            t.x = x;
            t.y = y;
            t.width = w;
            t.height = h;
            t.scaleX = scaleX;
            t.scaleY = scaleY;
            return t;
        }
        else
            return null;
    }
    syncTransform() {
        let t = this.getTargetTransform();
        if (t != null) {
            let style = this._visEle.style;
            style.width = t.width + 'px';
            style.height = t.height + 'px';
            PAL.browser.setStyleTransform(style, "scale(" + t.scaleX + "," + t.scaleY + ") rotate(" + (ILaya.stage.canvasDegree) + "deg)");
            this._container.style.left = t.x + 'px';
            this._container.style.top = t.y + 'px';
        }
    }
    createElements() {
        this._container = Browser.document.createElement("div");
        Browser.container.appendChild(this._container);
        let style = this._container.style;
        style.position = "absolute";
        style.zIndex = '1E5';
        this.initElement(this._eTextArea = Browser.document.createElement("textarea"));
        this.initElement(this._eInput = Browser.document.createElement("input"));
        this.initElement(this._ePassword = Browser.document.createElement("input"));
    }
    initElement(input) {
        let style = input.style;
        style.cssText = "position:absolute;overflow:hidden;resize:none;";
        style.resize = 'none';
        style.backgroundColor = 'transparent';
        style.border = 'none';
        style.outline = 'none';
        style.zIndex = '1';
        PAL.browser.setStyleTransformOrigin(style, "0 0");
        input.addEventListener('input', ev => !ev.isComposing && this.processInputting(ev));
        input.addEventListener("compositionend", ev => this.processInputting(ev));
        input.addEventListener('mousemove', ev => this.stopEvent(ev), { passive: false });
        input.addEventListener('mousedown', ev => this.stopEvent(ev), { passive: false });
        input.addEventListener('touchmove', ev => this.stopEvent(ev), { passive: false });
    }
    processInputting(ev) {
        if (!this.target)
            return;
        let ele = ev.target;
        let value = this.validateText(ele.value);
        ele.value = value;
        if (this.updateTargetText(value))
            this.target.event(Event.INPUT);
    }
    stopEvent(e) {
        if (e.type == 'touchmove')
            e.preventDefault();
        e.stopPropagation && e.stopPropagation();
    }
    onKeyDown(e) {
        if (e.key === "Enter" || e.key === "NumpadEnter") {
            let target = this.target;
            if (!target.multiline) {
                e.preventDefault();
                this._enterEvent.setTo(Event.ENTER, this.target, this.target);
                target.event(Event.ENTER, this._enterEvent);
                if (!this._enterEvent._defaultPrevented && this.target === target)
                    this.end();
            }
        }
    }
}
PAL.register("textInput", TextInputAdapter);
